import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { NotificationPreference, NotificationPreferencesResponse } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

interface UseNotificationPreferencesReturn {
  preferences: NotificationPreference[];
  loading: boolean;
  update: (preference: NotificationPreference) => Promise<void>;
}

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  // Bumped by every change, so a slow response doesn't overwrite a newer toggle.
  const seq = useRef(0);

  const refetch = useCallback(async () => {
    const started = seq.current;
    try {
      const result = await api.get<NotificationPreferencesResponse>('/api/notifications/preferences');
      if (started === seq.current) setPreferences(result.data);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Optimistic; on failure resync from the server.
  const update = useCallback(async (preference: NotificationPreference) => {
    seq.current++;
    setPreferences((prev) => prev.map((p) => (p.kind === preference.kind ? preference : p)));
    try {
      await api.put('/api/notifications/preferences', { preferences: [preference] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to save notification preferences');
      refetch();
    }
  }, [refetch]);

  return { preferences, loading, update };
}
